import { useCallback, useEffect, useRef } from 'react';
import type { SetStatePayload, TimUnreadMessageItem } from './tim-bridge';

export function createPageSessionId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function useTimPresence(
  userId: string | null | undefined,
  organizationId: string | null | undefined,
  ready: boolean,
  imVisible: boolean,
  send: (payload: SetStatePayload) => void
) {
  const current = useRef({ ready, imVisible });
  current.current = { ready, imVisible };
  const session = useRef<{ id: string; userId: string; scope: string; seq: number }>();
  const scope = `${userId || ''}:${organizationId || ''}`;

  useEffect(() => {
    if (!userId || !ready) return;
    const page = { id: createPageSessionId(), userId, scope, seq: 0 };
    session.current = page;
    const report = (hidden = false) => send({
      type: 'PRESENCE',
      userId,
      presence: {
        pageSessionId: page.id,
        seq: ++page.seq,
        pageVisible: !hidden && document.visibilityState === 'visible',
        toastCapable: !hidden && current.current.ready,
        imVisible: current.current.imVisible
      }
    });
    const update = () => report();
    const hide = () => report(true);
    report();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') report();
    }, 20_000);
    document.addEventListener('visibilitychange', update);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', update);
    window.addEventListener('online', update);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', update);
      window.removeEventListener('online', update);
      report(true);
      if (session.current === page) session.current = undefined;
    };
  }, [userId, scope, ready, send]);

  return useCallback((items: TimUnreadMessageItem[]) => {
    const page = session.current;
    if (!page || page.scope !== scope || !ready || document.visibilityState !== 'visible') return;
    const messages = items.flatMap((item) => {
      const msgSeq = item.message.sequence;
      return Number.isSafeInteger(msgSeq) && Number(msgSeq) > 0
        ? [{ timGroupId: item.timGroupId, msgSeq: Number(msgSeq) }]
        : [];
    });
    if (!messages.length) return;
    send({
      type: 'TOAST_RENDER_5S_ACK', userId: page.userId,
      ack: {
        pageSessionId: page.id, displayId: createPageSessionId(),
        shownAt: Date.now(), pageVisible: true, items: messages
      }
    });
  }, [scope, ready, send]);
}
